import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  IonModal, 
  IonHeader, 
  IonToolbar, 
  IonTitle, 
  IonButtons, 
  IonButton, 
  IonContent, 
  IonItem, 
  IonLabel, 
  IonInput, 
  IonTextarea, 
  IonSelect, 
  IonSelectOption
} from '@ionic/react'; 

const MedicalConcernsModal = React.memo(({ 
  isOpen, 
  onClose, 
  currentStep,
  medicalConcernData,
  setMedicalConcernData,
  setMedicalConcernId,
  setCurrentStep,
  handleAddMedicalConcern,
  handleAddTask,
  conciergeId,
  users,
  medicalConcernId
}) => {
  const [localFormData, setLocalFormData] = useState(medicalConcernData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRef = useRef(null);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (modalRef.current) {
        modalRef.current.remove();
      }
    };
  }, []);

  // Handle form data updates
  useEffect(() => {
    if (isOpen) {
      setLocalFormData(medicalConcernData);
      setIsSubmitting(false);
    }
  }, [isOpen, medicalConcernData]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    const updatedData = {
      ...localFormData,
      [name]: value
    };
    setLocalFormData(updatedData);
    setMedicalConcernData(updatedData); // Sync with parent state
  }, [localFormData, setMedicalConcernData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      if (currentStep === 1) {
        await handleAddMedicalConcern();
      } else {
        await handleAddTask(localFormData);
        onClose();
      }
    } catch (error) {
      console.error('Error in form submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, currentStep, localFormData, onClose, handleAddMedicalConcern, handleAddTask]);

  const handleModalDismiss = useCallback(() => {
    //console.log('Modal onDidDismiss - setting state to closed');
    if (modalRef.current) {
      modalRef.current.dismiss();
    }
    onClose();
    setCurrentStep(1);
    setMedicalConcernId(null);
    setMedicalConcernData({
      subject: '',
      description: '',
      status: 'Ongoing',
      date_identified: new Date().toISOString().split('T')[0],
      task: '',
      service_provider: '',
      due_date: new Date().toISOString().split('T')[0],
      person_responsible: '',
      metric_types_categories_id: '',
      task_service_provider: '',
      task_id: null,
      task_due_date: new Date().toISOString().split('T')[0],
      task_person_responsible: '',
      task_status: 'Ongoing',
      task_recurring_months: '',
      task_recurring: false,
      task_sent_to_salesforce: false,
      task_assigned_user_name: ''
    });
  }, [onClose, setCurrentStep, setMedicalConcernId, setMedicalConcernData]);

  return (
    <IonModal 
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={handleModalDismiss}
      breakpoints={[0, 1]}
      initialBreakpoint={1}
      backdropDismiss={!isSubmitting}
      showBackdrop={true}
      className="medical-concerns-modal"
      trigger="open-medical-concerns-modal"
    >
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>
            {medicalConcernId ? 
              (currentStep === 1 ? 'Edit Medical Concern' : 'Edit Task') : 
              (currentStep === 1 ? 'Add Medical Concern' : 'Add Task')}
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleModalDismiss} disabled={isSubmitting}>Close</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <form onSubmit={handleSubmit} className="medical-concerns-form">
          {currentStep === 1 ? (
            <>
              <IonItem className="ion-margin-bottom">
                <IonLabel position="stacked">Subject</IonLabel>
                <IonInput
                  name="subject"
                  value={localFormData.subject}
                  onIonChange={e => handleChange({ target: { name: 'subject', value: e.detail.value } })}
                  required
                  className="custom-input"
                />
              </IonItem>

              <IonItem className="ion-margin-bottom">
                <IonLabel position="stacked">Description</IonLabel>
                <IonTextarea
                  name="description"
                  value={localFormData.description}
                  onIonChange={e => handleChange({ target: { name: 'description', value: e.detail.value } })}
                  rows={4}
                  required
                  className="custom-textarea"
                />
              </IonItem>

              <IonItem className="ion-margin-bottom">
                <IonLabel position="stacked">Date Identified</IonLabel>
                <IonInput
                  name="date_identified"
                  type="date"
                  value={localFormData.date_identified}
                  onIonChange={e => handleChange({ target: { name: 'date_identified', value: e.detail.value } })}
                  required
                  className="custom-input"
                />
              </IonItem>

              <IonItem className="ion-margin-bottom">
                <IonLabel position="stacked">Status</IonLabel>
                <IonSelect
                  name="status"
                  value={localFormData.status}
                  onIonChange={e => handleChange({ target: { name: 'status', value: e.detail.value } })}
                  required
                  className="custom-select"
                >
                  <IonSelectOption value="Ongoing">Ongoing</IonSelectOption>
                  <IonSelectOption value="Complete">Complete</IonSelectOption>
                </IonSelect>
              </IonItem>
            </>
          ) : (
            <>
              <IonItem className="ion-margin-bottom">
                <IonLabel position="stacked">Task Description</IonLabel>
                <IonTextarea
                  name="task"
                  value={localFormData.task}
                  onIonChange={e => handleChange({ target: { name: 'task', value: e.detail.value } })}
                  rows={3}
                  required
                  placeholder="Describe the task to be completed"
                  className="custom-textarea"
                />
              </IonItem>

              <IonItem className="ion-margin-bottom">
                <IonLabel position="stacked">Service Provider</IonLabel>
                <IonInput
                  name="service_provider"
                  value={localFormData.task_service_provider}
                  onIonChange={e => handleChange({ target: { name: 'task_service_provider', value: e.detail.value } })}
                  required
                  placeholder="Enter service provider name"
                  className="custom-input"
                />
              </IonItem>

              <IonItem className="ion-margin-bottom">
                <IonLabel position="stacked">Due Date</IonLabel>
                <IonInput
                  name="due_date"
                  type="date"
                  value={localFormData.task_due_date}
                  onIonChange={e => handleChange({ target: { name: 'task_due_date', value: e.detail.value } })}
                  required
                  className="custom-input"
                />
              </IonItem>

              <IonItem className="ion-margin-bottom">
                <IonLabel position="stacked">Person Responsible</IonLabel>
                <IonSelect
                  name="person_responsible"
                  value={localFormData.task_person_responsible}
                  onIonChange={e => handleChange({ target: { name: 'task_person_responsible', value: e.detail.value } })}
                  required
                  className="custom-select"
                >
                  <IonSelectOption value="">Select a person</IonSelectOption>
                  {conciergeId && (
                    <IonSelectOption value={conciergeId.concierge_users_id}>
                      Default Concierge
                    </IonSelectOption>
                  )}
                  {users.map(user => (
                    <IonSelectOption key={user.id} value={user.id}>
                      {user.full_name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </>
          )}
          <div className="ion-padding-top">
            <IonButton expand="block" type="submit" color="primary">
              {currentStep === 1 ? 'Next' : 'Add Task'}
            </IonButton>
          </div>
        </form>
      </IonContent>
    </IonModal>
  );
});

export default MedicalConcernsModal; 